import React from 'react';
import { Box, Container, Typography, Paper, Grid, Card, Avatar, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Settings,
  Speed,
  DisplaySettings,
  People,
  Support,
  Rocket
} from '@mui/icons-material';
import Navigation from './Navigation';
import { aboutContent } from '../content/pages/about';

// Icon mapping for values section
const IconMap: Record<string, React.ReactElement> = {
  People: <People sx={{ fontSize: 32, color: 'white' }} />,
  Rocket: <Rocket sx={{ fontSize: 32, color: 'white' }} />,
  Support: <Support sx={{ fontSize: 32, color: 'white' }} />
};

// Icon mapping for "Why Choose" sidebar
const SmallIconMap: Record<string, React.ReactElement> = {
  TrendingUp: <TrendingUp sx={{ fontSize: 20 }} />,
  Speed: <Speed sx={{ fontSize: 20 }} />,
  DisplaySettings: <DisplaySettings sx={{ fontSize: 20 }} />,
  Settings: <Settings sx={{ fontSize: 20 }} />,
  Support: <Support sx={{ fontSize: 20 }} />,
  People: <People sx={{ fontSize: 20 }} />
};

const About: React.FC = () => {
  const c = aboutContent;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />

        {/* Navigation */}
        <Navigation />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', lineHeight: 1.4 }}>
              {c.hero.subtitle}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>

        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 6, boxShadow: 2, borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 3 }}>
                {c.howItStarted.title}
              </Typography>
              {c.howItStarted.paragraphs.map((para, i) => (
                <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
                  {para}
                </Typography>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 4, boxShadow: 2, borderRadius: 2, height: 'fit-content' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 3 }}>
                {c.whyChoose.title}
              </Typography>
              <Box sx={{ textAlign: 'left' }}>
                {c.whyChoose.items.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: '#4caf50', mr: 2, display: 'flex', alignItems: 'center' }}>
                      {SmallIconMap[item.iconName]}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#5A5856', lineHeight: 1.4 }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Company Values Section */}
        <Box sx={{ mt: 12, textAlign: 'center' }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 6 }}>
            {c.values.title}
          </Typography>
          <Typography variant="h6" sx={{ color: "#5A5856", mb: 6, maxWidth: 800, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}>
            {c.values.subtitle}
          </Typography>

          <Grid container spacing={4}>
            {c.values.cards.map((value, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 4, boxShadow: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 3, width: 64, height: 64 }}>
                    {IconMap[value.iconName]}
                  </Avatar>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#5A5856", fontSize: '1rem', lineHeight: 1.6 }}>
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box sx={{ mt: 12, textAlign: 'center' }}>
          <Paper sx={{ p: 6, bgcolor: '#4caf50', color: 'white', boxShadow: 3, borderRadius: 3 }}>
            <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              {c.cta.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: 700, mx: 'auto', lineHeight: 1.6 }}>
              {c.cta.subtitle}
            </Typography>
            <Button
              component={Link}
              to={c.cta.buttonLink}
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                color: 'white',
                borderColor: 'white',
                borderWidth: 2,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white',
                  borderWidth: 2
                }
              }}
            >
              {c.cta.buttonText}
            </Button>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default About;
